import { motion } from 'framer-motion';
import { Trophy, HelpCircle } from 'lucide-react';
import TeamLogo from '@/components/TeamLogo';
import { BracketPick } from '@/types/bracket';
import { isPlaceholderTeam } from '@/lib/placeholder-utils';

interface BracketMatchupSlotProps {
  slotId: string;
  round: string;
  teamHome: string | null;
  teamAway: string | null;
  pick?: BracketPick;
  isLocked: boolean;
  isViewingOther: boolean;
  isFinals?: boolean;
  isPropagated?: boolean;
  onPickWinner: (slotId: string, winner: string) => void;
  onTapForScore: (slotId: string) => void;
}

const BracketMatchupSlot = ({
  slotId,
  round,
  teamHome,
  teamAway,
  pick,
  isLocked,
  isViewingOther,
  isFinals = false,
  isPropagated = false,
  onTapForScore,
}: BracketMatchupSlotProps) => {
  const disabled = isLocked || isViewingOther;
  const teamsReady = teamHome && teamAway;

  // Placeholder matchups: at least one team is an undecided play-in seed
  const isPlaceholder = isPlaceholderTeam(teamHome) || isPlaceholderTeam(teamAway);

  const containerClass = isFinals
    ? 'bg-card border-2 border-accent/50 rounded-2xl p-4 relative shadow-[0_0_24px_rgba(234,88,12,0.2)]'
    : isPlaceholder && teamsReady
    ? 'bg-card border border-dashed border-border/60 rounded-2xl p-3 relative'
    : teamsReady
    ? 'bg-card border border-border rounded-2xl p-3 relative'
    : 'bg-card border border-dashed border-border rounded-2xl p-3 relative';

  // One-time border pulse for propagated slots: fades the accent border highlight after mount
  const propagatedBorderAnimation = isPropagated && teamsReady
    ? isFinals
      ? {
          // Finals already has accent border — use a brighter pulse
          borderColor: ['rgba(234,88,12,0.85)', 'rgba(234,88,12,0.5)'],
          transition: { duration: 1.5, repeat: 0 },
        }
      : {
          borderColor: ['rgba(234,88,12,0.6)', 'rgba(234,88,12,0.0)'],
          transition: { duration: 1.5, repeat: 0 },
        }
    : {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0, ...propagatedBorderAnimation }}
      transition={{ duration: 0.25 }}
      className={containerClass}
      onClick={() => { if (!disabled && teamsReady) onTapForScore(slotId); }}
      style={{ cursor: teamsReady && !disabled ? 'pointer' : 'default' }}
    >
      {/* Propagation indicator: subtle arrow shown when slot was auto-filled but not yet picked */}
      {isPropagated && teamsReady && !pick && (
        <span
          className="absolute top-1.5 left-2 text-[9px] text-accent/40 leading-none select-none"
          aria-hidden="true"
        >
          ←
        </span>
      )}

      {/* Trophy icon for Finals */}
      {isFinals && (
        <div className="flex justify-center mb-2">
          <Trophy className="w-5 h-5 text-accent/70" />
        </div>
      )}

      {!teamsReady ? (
        /* Waiting state */
        <div className="flex items-center justify-center py-4">
          <p className="text-muted-foreground text-xs">ממתין</p>
        </div>
      ) : (
        <>
          {/* Team rows — slide-in animation when propagated from upstream pick */}
          {[teamHome, teamAway].map((team) => {
            if (!team) return null;
            const isSelected = pick?.winner === team;
            const isTeamPlaceholder = isPlaceholderTeam(team);
            return (
              <motion.div
                key={team}
                initial={isPropagated ? { opacity: 0, x: -8 } : false}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className={`
                  flex items-center gap-2 rounded-xl px-2 py-2 mb-1
                  ${isSelected
                    ? 'bg-accent/15 border border-accent'
                    : 'border border-transparent'}
                `}
              >
                {isTeamPlaceholder
                  ? <HelpCircle size={28} className="shrink-0 text-muted-foreground/40" />
                  : <TeamLogo teamName={team} size={28} className="shrink-0" />
                }
                <span
                  className={`text-xs font-medium flex-1 text-right truncate ${
                    isSelected
                      ? 'text-foreground'
                      : isTeamPlaceholder
                      ? 'text-muted-foreground/65'
                      : 'text-muted-foreground'
                  }`}
                >
                  {team}
                </span>
              </motion.div>
            );
          })}

          {/* Score badge — if a score is already picked */}
          {pick?.predicted_score && (
            <div className="mt-1 pt-1 border-t border-border flex justify-center">
              <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/30">
                {pick.predicted_score}
              </span>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default BracketMatchupSlot;
